import { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { 
  Calendar, Clock, Users, AlertTriangle, RefreshCw, Trash2, 
  ArrowRight, Plus, User, Mail, Phone, ChevronDown, ChevronUp,
  History, AlertCircle, CheckCircle, X, Search, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RescheduleDashboard = ({ isOpen, onClose, bookings = [], classTypes = [], onRefreshBookings }) => {
  const [rescheduleQueue, setRescheduleQueue] = useState([]);
  const [expiredList, setExpiredList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Assignment dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [assignBookingId, setAssignBookingId] = useState('');
  
  // Create class dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createClassForm, setCreateClassForm] = useState({
    date: '',
    time: ''
  });
  
  // Reminder dialog
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderEntry, setReminderEntry] = useState(null);
  const [reminderType, setReminderType] = useState('30');
  const [customMessage, setCustomMessage] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);

  const handleSendReminder = async () => {
    if (!reminderEntry) return;
    
    setSendingReminder(true);
    try {
      await axios.post(`${API}/admin/reschedule/send-reminder`, {
        entry_id: reminderEntry.id,
        reminder_type: reminderType,
        custom_message: reminderType === 'custom' ? customMessage : null
      });
      
      toast.success(`Reminder sent to ${reminderEntry.student?.email}`);
      setShowReminderDialog(false);
      setCustomMessage('');
      fetchRescheduleQueue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const fetchRescheduleQueue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/reschedule/queue`);
      setRescheduleQueue(response.data || []);
    } catch (error) {
      console.error('Error fetching reschedule queue:', error);
      toast.error('Failed to load reschedule queue');
    }
  }, []);

  const fetchExpiredList = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/reschedule/expired`);
      setExpiredList(response.data || []);
    } catch (error) {
      console.error('Error fetching expired list:', error);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([fetchRescheduleQueue(), fetchExpiredList()])
        .finally(() => setLoading(false));
    }
  }, [isOpen, fetchRescheduleQueue, fetchExpiredList]);

  const handleRestoreExpired = async (entryId) => {
    try {
      await axios.post(`${API}/admin/reschedule/restore`, { entry_id: entryId });
      toast.success('Student restored to reschedule queue with new 60-day countdown');
      fetchRescheduleQueue();
      fetchExpiredList();
    } catch (error) {
      console.error('Error restoring entry:', error);
      toast.error('Failed to restore student');
    }
  };

  const handleAssignToClass = async () => {
    if (!selectedEntry || !assignBookingId) return;
    
    try {
      await axios.post(`${API}/admin/pending-queue/assign-to-class`, {
        queue_entry_id: selectedEntry.id,
        booking_id: assignBookingId
      });
      toast.success(`${selectedEntry.student?.first_name} assigned to class (marked as rescheduled)`);
      setShowAssignDialog(false);
      setSelectedEntry(null);
      setAssignBookingId('');
      fetchRescheduleQueue();
      if (onRefreshBookings) onRefreshBookings();
    } catch (error) {
      console.error('Error assigning to class:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign student');
    }
  };

  const handleCreateClass = async () => {
    if (!selectedEntry || !createClassForm.date || !createClassForm.time) return;
    
    try {
      await axios.post(`${API}/admin/pending-queue/create-class`, {
        queue_entry_ids: [selectedEntry.id],
        start_date: createClassForm.date,
        start_time: createClassForm.time
      });
      toast.success('New class created with rescheduled student');
      setShowCreateDialog(false);
      setSelectedEntry(null);
      setCreateClassForm({ date: '', time: '' });
      fetchRescheduleQueue();
      if (onRefreshBookings) onRefreshBookings();
    } catch (error) {
      console.error('Error creating class:', error);
      toast.error(error.response?.data?.detail || 'Failed to create class');
    }
  };

  const handleRemoveRescheduleTag = async (entryId) => {
    try {
      await axios.post(`${API}/admin/reschedule/unmark`, { queue_entry_id: entryId });
      toast.success('Reschedule tag removed');
      fetchRescheduleQueue();
    } catch (error) {
      console.error('Error removing tag:', error);
      toast.error('Failed to remove reschedule tag');
    }
  };

  // Filter entries by search
  const filteredQueue = rescheduleQueue.filter(entry => {
    const student = entry.student || {};
    const name = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase();
    const email = (student.email || '').toLowerCase();
    const classType = (entry.class_type_name || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || email.includes(search) || classType.includes(search);
  });

  const filteredExpired = expiredList.filter(entry => {
    const student = entry.student || {};
    const name = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase();
    const email = (student.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return name.includes(search) || email.includes(search);
  });

  // Get compatible bookings for assignment (same class type, approved status)
  const getCompatibleBookings = (entry) => {
    if (!entry) return [];
    return bookings.filter(b => 
      b.class_type_id === entry.class_type_id && 
      b.status === 'approved' &&
      new Date(b.start_time) > new Date()
    );
  };

  const getDaysRemainingColor = (days) => {
    if (days <= 7) return 'bg-red-500';
    if (days <= 15) return 'bg-amber-500';
    if (days <= 30) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  const formatOriginalDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return 'Unknown';
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-amber-500" />
            Reschedule Management
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Track and manage students needing to reschedule their classes
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="mb-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or class type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
            />
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-slate-800 border-slate-700">
              <TabsTrigger value="active" className="data-[state=active]:bg-amber-600">
                <Clock className="h-4 w-4 mr-2" />
                Active ({rescheduleQueue.length})
              </TabsTrigger>
              <TabsTrigger value="expired" className="data-[state=active]:bg-red-600">
                <History className="h-4 w-4 mr-2" />
                Expired ({expiredList.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="flex-1 overflow-hidden mt-4">
              <ScrollArea className="h-[50vh]">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-amber-500" />
                  </div>
                ) : filteredQueue.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No students pending reschedule</p>
                  </div>
                ) : (
                  <div className="space-y-3 pr-4">
                    {filteredQueue.map((entry) => {
                      const student = entry.student || {};
                      return (
                        <Card key={entry.id} className={`bg-slate-800/50 border-slate-700 ${entry.is_critical ? 'border-red-500' : entry.is_urgent ? 'border-amber-500' : ''}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-white">
                                    {student.first_name} {student.last_name}
                                  </h4>
                                  <Badge className={`${getDaysRemainingColor(entry.days_remaining)} text-white`}>
                                    {entry.days_remaining} days left
                                  </Badge>
                                  {entry.is_critical && (
                                    <Badge className="bg-red-900 text-red-200">
                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                      Critical
                                    </Badge>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-400">
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {student.email}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {student.phone || 'N/A'}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    Original: {formatOriginalDate(entry.original_class_date_for_reschedule)}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {entry.class_type_name}
                                  </div>
                                </div>

                                {/* Progress bar */}
                                <div className="mt-3">
                                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                                    <span>Day {entry.days_elapsed}</span>
                                    <span>60 days</span>
                                  </div>
                                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${getDaysRemainingColor(entry.days_remaining)} transition-all`}
                                      style={{ width: `${Math.min(100, (entry.days_elapsed / 60) * 100)}%` }}
                                    />
                                  </div>
                                </div>

                                {/* Reminder status */}
                                <div className="mt-3 p-2 bg-slate-700/30 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-slate-400">Email Reminders</span>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setReminderEntry(entry);
                                        setReminderType('30');
                                        setShowReminderDialog(true);
                                      }}
                                      className="h-6 text-xs border-slate-600 text-slate-300 hover:bg-slate-600"
                                    >
                                      <Send className="h-3 w-3 mr-1" />
                                      Send Reminder
                                    </Button>
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                      entry.reschedule_reminder_30_sent 
                                        ? 'bg-green-900/40 text-green-400 border border-green-700' 
                                        : 'bg-slate-700 text-slate-500 border border-slate-600'
                                    }`}>
                                      {entry.reschedule_reminder_30_sent ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                      30-day
                                      {entry.reschedule_reminder_30_sent_at && (
                                        <span className="text-[10px] opacity-70">
                                          ({new Date(entry.reschedule_reminder_30_sent_at).toLocaleDateString()})
                                        </span>
                                      )}
                                    </div>
                                    <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                      entry.reschedule_reminder_45_sent 
                                        ? 'bg-green-900/40 text-green-400 border border-green-700' 
                                        : 'bg-slate-700 text-slate-500 border border-slate-600'
                                    }`}>
                                      {entry.reschedule_reminder_45_sent ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                                      45-day
                                      {entry.reschedule_reminder_45_sent_at && (
                                        <span className="text-[10px] opacity-70">
                                          ({new Date(entry.reschedule_reminder_45_sent_at).toLocaleDateString()})
                                        </span>
                                      )}
                                    </div>
                                    {entry.reschedule_custom_reminder_sent && (
                                      <div className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-900/40 text-blue-400 border border-blue-700">
                                        <CheckCircle className="h-3 w-3" />
                                        Custom
                                        {entry.reschedule_custom_reminder_sent_at && (
                                          <span className="text-[10px] opacity-70">
                                            ({new Date(entry.reschedule_custom_reminder_sent_at).toLocaleDateString()})
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-col gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedEntry(entry);
                                    setShowAssignDialog(true);
                                  }}
                                  className="bg-teal-600 hover:bg-teal-500"
                                >
                                  <ArrowRight className="h-4 w-4 mr-1" />
                                  Assign to Class
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedEntry(entry);
                                    setShowCreateDialog(true);
                                  }}
                                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Create Class
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleRemoveRescheduleTag(entry.id)}
                                  className="text-slate-400 hover:text-red-400 hover:bg-slate-700"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Remove Tag
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            <TabsContent value="expired" className="flex-1 overflow-hidden mt-4">
              <ScrollArea className="h-[50vh]">
                {filteredExpired.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No expired reschedule entries</p>
                  </div>
                ) : (
                  <div className="space-y-3 pr-4">
                    {filteredExpired.map((entry) => {
                      const student = entry.student || {};
                      return (
                        <Card key={entry.id} className="bg-slate-800/50 border-red-900/50">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-white">
                                    {student.first_name} {student.last_name}
                                  </h4>
                                  <Badge className="bg-red-900 text-red-200">
                                    Expired
                                  </Badge>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-400">
                                  <div className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {student.email}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    Expired: {formatOriginalDate(entry.expired_at)}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    {entry.class_type_name}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <History className="h-3 w-3" />
                                    Original: {formatOriginalDate(entry.original_class_date_for_reschedule)}
                                  </div>
                                </div>
                              </div>

                              <Button
                                size="sm"
                                onClick={() => handleRestoreExpired(entry.id)}
                                className="bg-amber-600 hover:bg-amber-500"
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Restore
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Assign to Class Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Assign to Existing Class</DialogTitle>
              <DialogDescription className="text-slate-400">
                Select a class to assign {selectedEntry?.student?.first_name} {selectedEntry?.student?.last_name} to.
                They will be marked as "Rescheduled" and won't be eligible for another reschedule.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Select Class</Label>
                <Select value={assignBookingId} onValueChange={setAssignBookingId}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Choose a class..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {getCompatibleBookings(selectedEntry).map((booking) => (
                      <SelectItem key={booking.id} value={booking.id} className="text-white">
                        {format(parseISO(booking.start_time), 'MMM d, yyyy h:mm a')} - {booking.class_type_name}
                        ({booking.students?.length || 0} students)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {getCompatibleBookings(selectedEntry).length === 0 && (
                  <p className="text-amber-400 text-sm mt-2">
                    No future approved classes found for {selectedEntry?.class_type_name}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button 
                onClick={handleAssignToClass} 
                disabled={!assignBookingId}
                className="bg-teal-600 hover:bg-teal-500"
              >
                Assign to Class
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Class Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Create New Class</DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a new {selectedEntry?.class_type_name} class for {selectedEntry?.student?.first_name}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={createClassForm.date}
                  onChange={(e) => setCreateClassForm(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div>
                <Label>Time</Label>
                <Input
                  type="time"
                  value={createClassForm.time}
                  onChange={(e) => setCreateClassForm(prev => ({ ...prev, time: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button 
                onClick={handleCreateClass} 
                disabled={!createClassForm.date || !createClassForm.time}
                className="bg-teal-600 hover:bg-teal-500"
              >
                Create Class
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Reminder Dialog */}
        <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
          <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-teal-400" />
                Send Reschedule Reminder
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Send a reminder email to {reminderEntry?.student?.first_name} {reminderEntry?.student?.last_name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-300">Recipient</Label>
                <p className="text-sm text-slate-400 mt-1">{reminderEntry?.student?.email}</p>
              </div>
              
              <div>
                <Label className="text-slate-300">Reminder Type</Label>
                <Select value={reminderType} onValueChange={setReminderType}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="30" className="text-white">30-Day Reminder (Standard)</SelectItem>
                    <SelectItem value="45" className="text-white">45-Day Reminder (Urgent)</SelectItem>
                    <SelectItem value="custom" className="text-white">Custom Message</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {reminderType === 'custom' && (
                <div>
                  <Label className="text-slate-300">Custom Message</Label>
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Enter your custom message to the student..."
                    className="bg-slate-800 border-slate-700 text-white mt-1 min-h-[120px]"
                  />
                </div>
              )}
              
              <div className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-400">
                <p><strong>Student:</strong> {reminderEntry?.student?.first_name} {reminderEntry?.student?.last_name}</p>
                <p><strong>Class:</strong> {reminderEntry?.class_type_name}</p>
                <p><strong>Days Remaining:</strong> {reminderEntry?.days_remaining} days</p>
              </div>
            </div>

            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowReminderDialog(false)} 
                className="border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSendReminder}
                disabled={sendingReminder || (reminderType === 'custom' && !customMessage.trim())}
                className="bg-teal-600 hover:bg-teal-500"
              >
                {sendingReminder ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Reminder
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleDashboard;
